// ============================================================
//  ALTIMO Medical Center — ExpedientePaciente.jsx
//  Archivo clínico por paciente con código, historial mes/año
//  Ubicación: src/components/ExpedientePaciente.jsx
// ============================================================

import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import api from '../apiClient';
import PrescripcionesPanel from './PrescripcionesPanel';
import OrdenesLabPanel from './OrdenesLabPanel';
import IndicacionesEnfermeriaPanel from './IndicacionesEnfermeriaPanel';

const S = {
  pagina: { background: '#f0f4f8', minHeight: '100vh', padding: '20px', fontFamily: 'Segoe UI, sans-serif' },
  fila2:  { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' },
  card:   { background: '#fff', borderRadius: '10px', border: '1px solid #dde3ea', padding: '18px', marginBottom: '14px' },
  titulo: {
    fontSize: '12px', fontWeight: '700', color: '#1B2A5E',
    textTransform: 'uppercase', letterSpacing: '.05em',
    marginBottom: '14px', paddingBottom: '10px', borderBottom: '2px solid #e8f0fb',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  input:  { width: '100%', fontSize: '13px', padding: '8px 10px', border: '1px solid #c0ccda', borderRadius: '7px', background: '#fff', color: '#333', outline: 'none', boxSizing: 'border-box' },
  label:  { display: 'block', fontSize: '11px', fontWeight: '600', color: '#6c757d', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '.04em' },
  campo:  { marginBottom: '12px' },
  tabla:  { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th:     { textAlign: 'left', padding: '8px 12px', fontSize: '11px', color: '#6c757d', fontWeight: '600', borderBottom: '2px solid #e8f0fb', background: '#f8fafd' },
  td:     { padding: '9px 12px', borderBottom: '1px solid #eef1f6', color: '#333' },
  btnPrimario:   { background: '#1B2A5E', color: '#fff', border: 'none', borderRadius: '7px', padding: '8px 18px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  btnVerde:      { background: '#1D9E75', color: '#fff', border: 'none', borderRadius: '7px', padding: '7px 14px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
  btnSecundario: { background: '#fff', color: '#495057', border: '1px solid #c0ccda', borderRadius: '7px', padding: '7px 14px', fontSize: '12px', cursor: 'pointer' },
  codigo: { fontFamily: 'monospace', fontSize: '15px', fontWeight: '800', color: '#1B2A5E', background: '#e8f0fb', padding: '4px 12px', borderRadius: '6px', letterSpacing: '.1em' },
  pill: (color) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: '99px',
    fontSize: '10px', fontWeight: '700',
    background: { verde: '#d4edda', amber: '#fff3cd', azul: '#cce5ff', rojo: '#f8d7da', gris: '#e2e3e5' }[color] || '#e2e3e5',
    color:      { verde: '#155724', amber: '#856404', azul: '#004085', rojo: '#721c24', gris: '#383d41' }[color] || '#333',
  }),
  kpi: { background: '#fff', borderRadius: '8px', border: '1px solid #dde3ea', padding: '12px 16px', textAlign: 'center' },
  kpiVal:   { fontSize: '22px', fontWeight: '800', color: '#1B2A5E' },
  kpiLabel: { fontSize: '11px', color: '#6c757d', marginTop: '2px' },
};

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const HOY   = new Date();

// Mapeo de id de formulario → nombre legible
const NOMBRE_FORM = {
  ingreso_egreso:     'Hoja de ingreso y egreso',
  consentimiento:     'Consentimiento informado',
  ingreso_enfermera:  'Ingreso de enfermería',
  pagare:             'Pagaré sin protesto',
  evolucion:          'Evolución médica',
  ordenes_medico:     'Órdenes del médico',
  control_medicos:    'Control médicos tratantes',
  chequeo_quirurgico: 'Chequeo previo quirúrgico',
  signos_vitales:     'Signos vitales',
  reporte_enfermera:  'Reporte de enfermería',
  temp_pulso:         'Temperatura y pulso',
  balance_hidrico:    'Balance hídrico',
  tarjeta_medicamento:'Tarjeta de medicamentos',
  tarjeta_oral:       'Tarjeta vía oral',
  tarjeta_parenteral: 'Tarjeta parenteral',
  prog_cirugias:      'Programación de cirugías',
  gastos_sala:        'Gastos sala de operación',
  reporte_anestesia:  'Reporte de anestesia',
  reporte_operatorio: 'Reporte operatorio',
  honorarios:         'Honorarios médicos',
  asignaciones:       'Asignaciones',
  reporte_insumos:    'Reporte de insumos',
  req_productos:      'Requisición de productos',
  control_temp_refri: 'Control temperatura refrigeradora',
  lista_asistencia:   'Lista de asistencia',
  acciones_personal:  'Acciones de personal',
};

// ── Componente firma: usa base64 ya incluido en la nota (v3) ────
// La imagen ya viene en n.firma_b64 desde el handler notas:listar
// No necesita llamada IPC adicional
function FirmaNotaImg({ b64, nombre, titulo }) {
  if (!b64) return null;
  return (
    <div style={{ marginTop:'8px', paddingTop:'8px', borderTop:'1px solid #eef1f6', display:'flex', alignItems:'center', gap:'10px' }}>
      <img src={b64} alt="Firma y sello"
        style={{ maxHeight:'64px', maxWidth:'220px', objectFit:'contain', display:'block' }} />
    </div>
  );
}

// ── Vista de detalle de un paciente ─────────────────────────
function DetallePaciente({ paciente, usuario, onVolver, onVerAdmision, onAbrirFormulario, irASeccion }) {
  // Financiero — solo admin/caja deben ver montos facturados, igual que en
  // el Dashboard y en las Alertas del sistema. El resto de roles que acceden
  // a Expedientes (médico, jefe_medico, enfermería, etc.) son clínicos.
  const verFacturacion = ['admin', 'caja'].includes(usuario?.rol);

  const [historial,      setHistorial]      = useState([]);
  const [cargando,       setCargando]       = useState(true);
  const [anioFiltro,     setAnioFiltro]     = useState('');
  const [mesFiltro,      setMesFiltro]      = useState('');
  const [cuentaTotales,  setCuentaTotales]  = useState({});

  // ── Formularios por admisión
  const [expandedAdm,    setExpandedAdm]    = useState(null);
  const [formulariosAdm, setFormulariosAdm] = useState([]);
  const [cargandoForms,  setCargandoForms]  = useState(false);

  // ── Laboratorio por admisión
  const [expandedLab,    setExpandedLab]    = useState(null);
  const [labAdm,         setLabAdm]         = useState([]);
  const [cargandoLab,    setCargandoLab]    = useState(false);

  // ── Navegación dirigida: scroll + resaltado momentáneo al panel de
  // Estudios Clínicos cuando se llega desde "Mi panel" → Labs pendientes.
  const ordenesLabRef    = useRef(null);
  const [resaltarLabs,   setResaltarLabs]   = useState(false);

  // ── Notas clínicas persistentes (v3) ──
  const [notas,          setNotas]          = useState([]);
  const [nuevaNota,      setNuevaNota]      = useState('');
  const [guardandoNota,  setGuardandoNota]  = useState(false);
  const [firmaSelloImg,  setFirmaSelloImg]  = useState(null);

  // ── Expediente foleado (PDF legal único por admisión) ──
  const [generandoFoleado, setGenerandoFoleado] = useState(null); // admision_id en curso, o null

  useEffect(() => {
    if (irASeccion !== 'labs' || !ordenesLabRef.current) return;
    // Pequeño delay: esperar a que el historial (con la admisión activa)
    // termine de montar OrdenesLabPanel antes de hacer scroll hacia él.
    const t = setTimeout(() => {
      ordenesLabRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setResaltarLabs(true);
      setTimeout(() => setResaltarLabs(false), 2200);
    }, 250);
    return () => clearTimeout(t);
  }, [irASeccion, historial]);

  useEffect(() => {
    const cargar = async () => {
      setCargando(true);
      const r = await api.expedientes.historial(paciente.id);
      if (r.ok) {
        setHistorial(r.data);
        const totales = {};
        await Promise.all(r.data.map(async (a) => {
          const rt = await api.cuenta.total(a.id);
          if (rt.ok && rt.data) totales[a.id] = rt.data.total || 0;
        }));
        setCuentaTotales(totales);
      }
      setCargando(false);
    };
    cargar();

    // Cargar notas persistentes del paciente (requiere main.js v3 actualizado)
    const cargarNotas = async () => {
      try {
        const rn = await api.notas.listar({ paciente_id: paciente.id });
        if (rn?.ok && Array.isArray(rn.data)) setNotas(rn.data);
      } catch(e) { console.warn('[notas] Error cargando:', e.message); }
    };
    cargarNotas();

    // Cargar imagen firma+sello del usuario actual para preview al registrar nota
    if (usuario?.id) {
      try {
        api.notas.leerImagenFirma({ usuario_id: usuario.id })
          .then(r => {
            if (typeof r === 'string' && r.startsWith('data:'))
              setFirmaSelloImg(r);
            else if (r?.ok && r.data && typeof r.data === 'string' && r.data.startsWith('data:'))
              setFirmaSelloImg(r.data);
          })
          .catch(() => {});
      } catch(_) {}
    }
  }, [paciente.id]);

  const toggleFormularios = async (admId) => {
    if (expandedAdm === admId) {
      setExpandedAdm(null);
      setFormulariosAdm([]);
      return;
    }
    setExpandedAdm(admId);
    setCargandoForms(true);
    const r = await api.formularios.listarAdmision(admId);
    if (r.ok) setFormulariosAdm(r.data);
    setCargandoForms(false);
  };

  // Abrir PDF de formulario guardado en disco
  const abrirPdfFormulario = async (admisionId, formularioId) => {
    // Antes: window.altimo.formulariosDatos.abrirPdf() usaba shell.openPath()
    // de Electron. Ahora el servidor confirma que el PDF existe y devuelve
    // su URL pública; se abre en una pestaña nueva del navegador.
    const r = await api.formularios.abrirPdf({ admision_id: admisionId, formulario_id: formularioId });
    if (r?.ok && r.data?.url) {
      window.open(api.lab.urlArchivo(r.data.url), '_blank');
    } else {
      alert('PDF no encontrado. Guarda el formulario primero para generarlo.');
    }
  };

  const toggleLab = async (admId) => {
    if (expandedLab === admId) { setExpandedLab(null); setLabAdm([]); return; }
    // Cerrar forms si estaba abierto
    if (expandedAdm === admId) { setExpandedAdm(null); setFormulariosAdm([]); }
    setExpandedLab(admId);
    setCargandoLab(true);
    const r = await api.lab.listar(admId);
    if (r.ok) setLabAdm(r.data);
    setCargandoLab(false);
  };

  const filtrado = historial.filter(a => {
    const fecha = new Date(a.fecha_ingreso);
    const okAnio = !anioFiltro || fecha.getFullYear() === parseInt(anioFiltro);
    const okMes  = !mesFiltro  || (fecha.getMonth() + 1) === parseInt(mesFiltro);
    return okAnio && okMes;
  });

  const aniosDisponibles = [...new Set(historial.map(a => new Date(a.fecha_ingreso).getFullYear()))].sort((a,b) => b-a);
  const estadoColor = { Activa: 'verde', Alta: 'amber', Cerrada: 'azul' };

  // ── Guardar nota clínica persistente ─────────────────────────
  const guardarNota = async () => {
    if (!nuevaNota.trim() || !usuario?.id) return;
    setGuardandoNota(true);
    try {
      const admActiva = historial.find(a => a.estado === 'Activa');
      const tipoNota = ['jefe_medico','medico'].includes(usuario.rol)    ? 'medico'
        : usuario.rol === 'jefa_enfermeria'                              ? 'jefa_enfermeria'
        : usuario.rol === 'supervisora_turno'                            ? 'supervisora'
        : 'enfermeria';
      const r = await api.notas.guardar({
        paciente_id: paciente.id,
        admision_id: admActiva?.id || null,
        tipo:        tipoNota,
        nota:        nuevaNota.trim(),
        usuario_id:  usuario.id,
      });
      if (r?.ok) {
        setNuevaNota('');
        // Recargar todas las notas (incluyendo base64 de firmas)
        const rn = await api.notas.listar({ paciente_id: paciente.id });
        if (rn?.ok && Array.isArray(rn.data)) setNotas(rn.data);
      }
    } catch(e) { console.error('[guardarNota]', e.message); }
    setGuardandoNota(false);
  };

  const TIPO_NOTA_COLOR = {
    medico:          { bg:'#e2d9f3', color:'#4a2080', label:'Médico'             },
    jefe_medico:     { bg:'#c8d8f8', color:'#1B2A5E', label:'Jefe Médico'        },
    jefa_enfermeria: { bg:'#a8dbe5', color:'#064e58', label:'Jefa de Enfermería' },
    supervisora:     { bg:'#e0d0f0', color:'#5a3a6d', label:'Supervisora'        },
    enfermeria:      { bg:'#d1ecf1', color:'#0c5460', label:'Enfermería'         },
    general:         { bg:'#e2e3e5', color:'#383d41', label:'General'            },
  };

  // ── PDF: historial + TODAS las notas con firma incrustada ─────
  const imprimirHistorial = () => {
    const filas = filtrado.map(a => `
      <tr>
        <td>${new Date(a.fecha_ingreso).toLocaleDateString('es-SV')}</td>
        <td>${a.tipo_ingreso}</td>
        <td>${a.habitacion_num ? a.habitacion_num + ' · ' + a.habitacion_tipo : 'Ambulatorio'}</td>
        <td>${a.medico_nombre || '—'}</td>
        <td>${a.diagnostico || '—'}</td>
        <td>${a.estado}</td>
        <td>$${Number(a.total_facturado || 0).toFixed(2)}</td>
      </tr>`).join('');

    // HTML de notas — la firma viene en base64 directamente (n.firma_b64)
    const notasHTML = notas.map(n => {
      const tn    = TIPO_NOTA_COLOR[n.tipo] || { color:'#495057', bg:'#e2e3e5', label: n.tipo };
      const fecha = new Date(n.fecha_hora).toLocaleString('es-SV', { dateStyle:'short', timeStyle:'short' });
      const autor = `${n.autor_titulo ? n.autor_titulo + ' ' : ''}${n.autor_nombre}`;
      const firmaTag = n.firma_b64
        ? `<div style="margin-top:8px;padding-top:8px;border-top:1px solid #eee">
             <img src="${n.firma_b64}" style="max-height:50px;max-width:190px;object-fit:contain;display:block"/>
           </div>`
        : '';
      return `
        <div style="border:1px solid #dde3ea;border-left:4px solid ${tn.color};border-radius:6px;
                    padding:10px 14px;margin-bottom:10px;background:#fff;page-break-inside:avoid">
          <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:7px">
            <div style="display:flex;align-items:center;gap:8px">
              <span style="font-size:9px;font-weight:700;color:${tn.color};background:${tn.bg};
                           padding:2px 9px;border-radius:99px">${tn.label}</span>
              <span style="font-size:11px;color:#1B2A5E;font-weight:700">${autor}</span>
            </div>
            <span style="font-size:10px;color:#6c757d;white-space:nowrap">${fecha}</span>
          </div>
          <p style="font-size:12px;color:#333;margin:0;line-height:1.7;white-space:pre-wrap">${n.nota}</p>
          ${firmaTag}
        </div>`;
    }).join('');

    const html = `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"/>
    <title>Expediente — ${paciente.nombre} ${paciente.apellido}</title>
    <style>
      *{box-sizing:border-box;margin:0;padding:0}
      body{font-family:'Segoe UI',sans-serif;padding:22px;color:#222;font-size:12px}
      h2{color:#1B2A5E;font-size:13px;font-weight:700;text-transform:uppercase;
         letter-spacing:.04em;margin:18px 0 10px;padding-bottom:5px;
         border-bottom:2px solid #e8f0fb}
      table{width:100%;border-collapse:collapse;font-size:11px;margin-bottom:6px}
      th{background:#1B2A5E;color:#fff;padding:6px 9px;text-align:left;font-size:10px}
      td{padding:5px 9px;border-bottom:1px solid #eee}
      .cab{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:12px}
      .codigo{font-family:monospace;font-weight:800;color:#1B2A5E;background:#e8f0fb;
              padding:2px 8px;border-radius:4px}
      .meta{font-size:12px;color:#495057;margin-bottom:4px}
      @media print{body{padding:12px}.no-print{display:none}}
    </style>
    </head><body>
    <div class="cab">
      <div>
        <div style="font-size:22px;font-weight:900;color:#1B2A5E;line-height:1">ALTIMO</div>
        <div style="font-size:9px;letter-spacing:2px;color:#1D9E75;font-weight:700">MEDICAL CENTER</div>
      </div>
      <div style="text-align:right">
        <div style="font-size:14px;font-weight:700;color:#1B2A5E">EXPEDIENTE CLÍNICO</div>
        <div style="font-size:11px;color:#666">Impreso: ${new Date().toLocaleString('es-SV')}</div>
      </div>
    </div>
    <hr style="border:none;border-top:2px solid #1B2A5E;margin:8px 0 12px"/>

    <div class="meta"><strong>Paciente:</strong> ${paciente.nombre} ${paciente.apellido}
      &nbsp;&nbsp; <strong>Código:</strong> <span class="codigo">${paciente.codigo || 'PAC-' + String(paciente.id).padStart(5,'0')}</span></div>
    <div class="meta"><strong>DUI:</strong> ${paciente.dui || '—'}
      &nbsp;&nbsp; <strong>Tel:</strong> ${paciente.telefono || '—'}
      &nbsp;&nbsp; <strong>Seguro:</strong> ${paciente.seguro_nombre || 'Ninguno'}</div>

    <h2>Historial de atenciones (${filtrado.length} registros)</h2>
    <table>
      <thead><tr><th>Fecha</th><th>Tipo</th><th>Habitación</th><th>Médico</th><th>Diagnóstico</th><th>Estado</th><th>Facturado</th></tr></thead>
      <tbody>${filas}</tbody>
    </table>

    ${notas.length > 0 ? `
    <h2>Notas Clínicas — Registro completo (${notas.length} notas)</h2>
    ${notasHTML}` : ''}

    <script>window.onload = () => window.print()</script>
    </body></html>`;

    const w = window.open('', '_blank', 'width=920,height=750');
    if (w) { w.document.write(html); w.document.close(); }
  };

  // ── Expediente legal completo y foleado de una admisión ──────
  const generarExpedienteFoleado = async (admisionId) => {
    if (generandoFoleado) return; // evita doble click mientras genera
    setGenerandoFoleado(admisionId);
    try {
      const r = await api.expedientes.generarPdfFoleado(admisionId);
      if (r?.ok) {
        const { totalPaginas, documentosIncluidos, documentosNoIncorporados, path: rutaFoleado } = r.data;
        let msg = `Expediente generado: ${totalPaginas} páginas, ${documentosIncluidos} documentos incorporados.`;
        if (documentosNoIncorporados?.length) {
          msg += `\n\nAviso: ${documentosNoIncorporados.length} archivo(s) no se pudieron incorporar (formato .doc/.docx no soportado para fusión):\n` +
                 documentosNoIncorporados.join('\n');
        }
        alert(msg);
        // Antes: window.altimo.expedientes.abrirPdfFoleado() usaba shell.openPath()
        // de Electron. Ahora se abre la URL pública en una pestaña nueva.
        window.open(api.lab.urlArchivo(rutaFoleado), '_blank');
      } else {
        alert('No se pudo generar el expediente foleado.');
      }
    } catch (e) {
      alert(`Error al generar expediente foleado: ${e.message}`);
    } finally {
      setGenerandoFoleado(null);
    }
  };

  return (
    <div>
      <button style={{ ...S.btnSecundario, marginBottom: '14px' }} onClick={onVolver}>
        ← Volver a la lista
      </button>

      {/* Encabezado del paciente */}
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: '20px', fontWeight: '800', color: '#1B2A5E', marginBottom: '4px' }}>
              {paciente.nombre} {paciente.apellido}
            </div>
            <div style={{ marginBottom: '8px' }}>
              <span style={S.codigo}>
                {paciente.codigo || 'PAC-' + String(paciente.id).padStart(5,'0')}
              </span>
            </div>
            <div style={{ fontSize: '13px', color: '#6c757d', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <span>DUI: {paciente.dui || '—'}</span>
              <span>Tel: {paciente.telefono || '—'}</span>
              <span>Seguro: {paciente.seguro_nombre || 'Ninguno'}</span>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: verFacturacion ? '1fr 1fr 1fr' : '1fr 1fr', gap: '10px', minWidth: verFacturacion ? '280px' : '190px' }}>
            <div style={S.kpi}><div style={S.kpiVal}>{historial.length}</div><div style={S.kpiLabel}>Total visitas</div></div>
            <div style={S.kpi}><div style={S.kpiVal}>{historial.filter(a => a.estado === 'Activa').length}</div><div style={S.kpiLabel}>Activas</div></div>
            {/* Financiero — solo admin/caja, igual que en la lista general y en el Dashboard */}
            {verFacturacion && (
              <div style={S.kpi}><div style={{ ...S.kpiVal, fontSize: '14px' }}>${historial.reduce((s,a) => s + Number(a.total_facturado || 0), 0).toFixed(2)}</div><div style={S.kpiLabel}>Total facturado</div></div>
            )}
          </div>
        </div>
      </div>

      {/* ══ NOTAS CLÍNICAS PERSISTENTES (v3) ══ */}
      <div style={S.card}>
        <div style={S.titulo}>
          <span>📋 Notas Clínicas del Expediente ({notas.length})</span>
          <span style={{ fontSize:'11px', color:'#6c757d', fontWeight:'400' }}>
            Se mantienen en el expediente permanentemente
          </span>
        </div>

        {/* Lista de notas ya registradas */}
        {notas.length === 0 ? (
          <div style={{ color:'#adb5bd', fontSize:'13px', padding:'12px 0', textAlign:'center' }}>
            No hay notas registradas aún para este paciente.
          </div>
        ) : (
          <div style={{ marginBottom:'16px', maxHeight:'420px', overflowY:'auto', paddingRight:'4px' }}>
            {notas.map(n => {
              const tn = TIPO_NOTA_COLOR[n.tipo] || { bg:'#e2e3e5', color:'#383d41', label: n.tipo };
              return (
                <div key={n.id} style={{
                  border:`1px solid #dde3ea`, borderLeft:`4px solid ${tn.color}`,
                  borderRadius:'8px', padding:'11px 14px', marginBottom:'8px', background:'#fff',
                }}>
                  {/* Cabecera de la nota */}
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'8px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                      <span style={{ fontSize:'10px', fontWeight:'700', color: tn.color, background: tn.bg, padding:'2px 9px', borderRadius:'99px' }}>
                        {tn.label}
                      </span>
                      <span style={{ fontSize:'12px', fontWeight:'700', color:'#1B2A5E' }}>
                        {n.autor_titulo ? `${n.autor_titulo} ` : ''}{n.autor_nombre}
                      </span>
                    </div>
                    <span style={{ fontSize:'10px', color:'#adb5bd', whiteSpace:'nowrap', marginLeft:'8px' }}>
                      {new Date(n.fecha_hora).toLocaleString('es-SV', { dateStyle:'short', timeStyle:'short' })}
                    </span>
                  </div>
                  {/* Cuerpo de la nota */}
                  <p style={{ fontSize:'13px', color:'#333', margin:'0 0 8px', lineHeight:'1.7', whiteSpace:'pre-wrap' }}>
                    {n.nota}
                  </p>
                  {/* Firma+sello — base64 ya incluido en la nota, sin IPC adicional */}
                  {n.firma_b64 && (
                    <FirmaNotaImg b64={n.firma_b64} nombre={n.autor_nombre} titulo={n.autor_titulo} />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Formulario para nueva nota */}
        <div style={{ borderTop:'2px solid #e8f0fb', paddingTop:'14px' }}>
          <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#1B2A5E', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:'6px' }}>
            Nueva nota — {usuario?.nombre || 'Usuario'} ({usuario?.rol})
          </label>
          <textarea
            value={nuevaNota}
            onChange={e => setNuevaNota(e.target.value)}
            rows={3}
            style={{ ...S.input, resize:'vertical', lineHeight:'1.6', fontFamily:'inherit' }}
            placeholder="Escribe la nota clínica (evolución, observación, indicación...)..."
          />
          <div style={{ display:'flex', alignItems:'center', gap:'10px', marginTop:'8px' }}>
            <button
              style={{ ...S.btnVerde, opacity: !nuevaNota.trim() ? 0.5 : 1 }}
              onClick={guardarNota}
              disabled={guardandoNota || !nuevaNota.trim()}>
              {guardandoNota ? 'Guardando...' : '✔ Guardar nota'}
            </button>
            {firmaSelloImg && (
              <div style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'11px', color:'#1D9E75' }}>
                <img src={firmaSelloImg} alt="Firma" style={{ maxHeight:'32px', maxWidth:'100px', objectFit:'contain' }} />
                <span>Firma se adjuntará automáticamente</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ══ PRESCRIPCIONES (médico → farmacia) — solo con admisión activa ══ */}
      {historial.find(a => a.estado === 'Activa') && (
        <PrescripcionesPanel
          admision_id={historial.find(a => a.estado === 'Activa').id}
          medico_id={historial.find(a => a.estado === 'Activa').medico_id}
          usuario={usuario}
        />
      )}

      {/* ══ ÓRDENES DE LABORATORIO (médico → laboratorio) — solo con admisión activa ══ */}
      {historial.find(a => a.estado === 'Activa') && (
        <div ref={ordenesLabRef}
          style={{
            borderRadius: '12px',
            transition: 'box-shadow .3s ease, background .3s ease',
            boxShadow: resaltarLabs ? '0 0 0 3px #1D9E75' : 'none',
            background: resaltarLabs ? '#eafaf3' : 'transparent',
          }}>
          <OrdenesLabPanel
            admision_id={historial.find(a => a.estado === 'Activa').id}
            medico_id={historial.find(a => a.estado === 'Activa').medico_id}
            usuario={usuario}
          />
        </div>
      )}

      {/* ══ INDICACIONES PARA ENFERMERÍA (médico → enfermería → médico) — solo con admisión activa ══ */}
      {historial.find(a => a.estado === 'Activa') && (
        <IndicacionesEnfermeriaPanel
          admision_id={historial.find(a => a.estado === 'Activa').id}
          medico_id={historial.find(a => a.estado === 'Activa').medico_id}
          usuario={usuario}
        />
      )}

      {/* Historial de admisiones */}
      <div style={S.card}>
        <div style={S.titulo}>
          <span>Historial de atenciones ({filtrado.length})</span>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <select style={{ ...S.input, width: 'auto', fontSize: '12px' }}
              value={anioFiltro} onChange={e => setAnioFiltro(e.target.value)}>
              <option value="">Todos los años</option>
              {aniosDisponibles.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <select style={{ ...S.input, width: 'auto', fontSize: '12px' }}
              value={mesFiltro} onChange={e => setMesFiltro(e.target.value)}>
              <option value="">Todos los meses</option>
              {MESES.map((m,i) => <option key={i} value={i+1}>{m}</option>)}
            </select>
            <button style={S.btnVerde} onClick={imprimirHistorial}>Imprimir PDF</button>
          </div>
        </div>

        {cargando ? (
          <div style={{ textAlign: 'center', padding: '30px', color: '#6c757d' }}>Cargando historial...</div>
        ) : filtrado.length === 0 ? (
          <p style={{ color: '#6c757d', fontSize: '13px' }}>No hay atenciones para el filtro seleccionado.</p>
        ) : (
          <table style={S.tabla}>
            <thead>
              <tr>
                {['# Adm.', 'Fecha ingreso', 'Tipo', 'Habitación', 'Médico', 'Diagnóstico', 'Estado', 'Facturado', 'Acciones'].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtrado.map(a => (
                <Fragment key={a.id}>
                  <tr key={a.id}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafd'}
                    onMouseLeave={e => e.currentTarget.style.background = expandedAdm === a.id ? '#f0f7ff' : 'transparent'}
                    style={{ background: expandedAdm === a.id ? '#f0f7ff' : 'transparent' }}>
                    <td style={{ ...S.td, fontWeight: '700', color: '#1B2A5E' }}>#{a.id}</td>
                    <td style={S.td}>{new Date(a.fecha_ingreso).toLocaleDateString('es-SV')}</td>
                    <td style={S.td}>{a.tipo_ingreso}</td>
                    <td style={S.td}>{a.habitacion_num ? `${a.habitacion_num} · ${a.habitacion_tipo}` : 'Ambulatorio'}</td>
                    <td style={S.td}>{a.medico_nombre || '—'}</td>
                    <td style={{ ...S.td, maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {a.diagnostico || '—'}
                    </td>
                    <td style={S.td}>
                      <span style={S.pill(estadoColor[a.estado] || 'gris')}>{a.estado}</span>
                    </td>
                    <td style={{ ...S.td, fontWeight: '700', color: '#1B2A5E' }}>
                      {/* Muestra total de cuenta actualizado, no solo lo facturado */}
                      <span style={{ color: cuentaTotales[a.id] > 0 ? '#1B2A5E' : '#adb5bd' }}>
                        ${Number(cuentaTotales[a.id] ?? a.total_facturado ?? 0).toFixed(2)}
                      </span>
                    </td>
                    <td style={S.td}>
                      <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                        <button style={{ ...S.btnSecundario, fontSize: '11px', padding: '4px 9px' }}
                          onClick={() => onVerAdmision(a.id)}>
                          Cuenta
                        </button>
                        <button
                          style={{
                            fontSize: '11px', padding: '4px 9px', borderRadius: '7px',
                            border: 'none', cursor: 'pointer', fontWeight: '600',
                            background: expandedAdm === a.id ? '#1B2A5E' : '#e8f0fb',
                            color:      expandedAdm === a.id ? '#fff'    : '#1B2A5E',
                          }}
                          onClick={() => { setExpandedLab(null); setLabAdm([]); toggleFormularios(a.id); }}>
                          {expandedAdm === a.id ? '▲ Forms' : '▼ Forms'}
                        </button>
                        <button
                          style={{
                            fontSize: '11px', padding: '4px 9px', borderRadius: '7px',
                            border: 'none', cursor: 'pointer', fontWeight: '600',
                            background: expandedLab === a.id ? '#0F6E56' : '#e1f5ee',
                            color:      expandedLab === a.id ? '#fff'    : '#0F6E56',
                          }}
                          onClick={() => { setExpandedAdm(null); setFormulariosAdm([]); toggleLab(a.id); }}>
                          {expandedLab === a.id ? '▲ Lab' : '🔬 Lab'}
                        </button>
                        <button
                          title="Genera el PDF legal único de esta admisión, con folio de página"
                          disabled={generandoFoleado === a.id}
                          style={{
                            fontSize: '11px', padding: '4px 9px', borderRadius: '7px',
                            border: 'none', cursor: generandoFoleado === a.id ? 'wait' : 'pointer',
                            fontWeight: '600',
                            background: generandoFoleado === a.id ? '#c0ccda' : '#fff3cd',
                            color:      generandoFoleado === a.id ? '#6c757d' : '#856404',
                            opacity:    generandoFoleado === a.id ? 0.7 : 1,
                          }}
                          onClick={() => generarExpedienteFoleado(a.id)}>
                          {generandoFoleado === a.id ? '⏳ Generando...' : '📋 Expediente'}
                        </button>
                      </div>
                    </td>
                  </tr>

                  {/* ── Fila expandible: formularios de esta admisión ── */}
                  {expandedAdm === a.id && (
                    <tr key={`forms-${a.id}`}>
                      <td colSpan="9" style={{
                        padding: '12px 18px',
                        background: '#f0f7ff',
                        borderBottom: '2px solid #c8ddf7',
                      }}>
                        <div style={{ fontSize: '11px', fontWeight: '700', color: '#1B2A5E', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                          Formularios clínicos — Admisión #{a.id}
                        </div>
                        {cargandoForms ? (
                          <span style={{ fontSize: '12px', color: '#6c757d' }}>Cargando...</span>
                        ) : formulariosAdm.length === 0 ? (
                          <span style={{ fontSize: '12px', color: '#adb5bd', fontStyle: 'italic' }}>
                            Sin formularios guardados para esta admisión.
                          </span>
                        ) : (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {formulariosAdm.map(f => (
                              <div key={f.formulario_id} style={{
                                background: '#fff', border: '1px solid #c8ddf7',
                                borderLeft: '3px solid #1B2A5E',
                                borderRadius: '7px', padding: '8px 12px',
                                minWidth: '190px', display: 'flex', flexDirection: 'column', gap: '4px',
                              }}>
                                <div style={{ fontWeight: '700', color: '#1B2A5E', fontSize: '12px', marginBottom: '2px' }}>
                                  {NOMBRE_FORM[f.formulario_id] || f.formulario_id}
                                </div>
                                <div style={{ color: '#6c757d', fontSize: '10px', marginBottom: '5px' }}>
                                  {new Date(f.actualizado_en).toLocaleString('es-SV')}
                                  {f.usuario_nombre && ` · ${f.usuario_nombre}`}
                                </div>
                                <div style={{ display: 'flex', gap: '5px' }}>
                                  <button
                                    style={{ background: '#1D9E75', color: '#fff', border: 'none', borderRadius: '5px', padding: '4px 10px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}
                                    onClick={() => abrirPdfFormulario(a.id, f.formulario_id)}>
                                    Ver PDF
                                  </button>
                                  {onAbrirFormulario && (
                                    <button
                                      style={{ background: '#1B2A5E', color: '#fff', border: 'none', borderRadius: '5px', padding: '4px 10px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}
                                      onClick={() => onAbrirFormulario(a.id, f.formulario_id)}>
                                      Editar →
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}

                  {/* ── Fila expandible: resultados de laboratorio ── */}
                  {expandedLab === a.id && (
                    <tr key={`lab-${a.id}`}>
                      <td colSpan="9" style={{
                        padding: '12px 18px',
                        background: '#f0faf6',
                        borderBottom: '2px solid #9fe1cb',
                      }}>
                        <div style={{ fontSize: '11px', fontWeight: '700', color: '#0F6E56', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                          🔬 Resultados de laboratorio — Admisión #{a.id}
                        </div>
                        {cargandoLab ? (
                          <span style={{ fontSize: '12px', color: '#6c757d' }}>Cargando...</span>
                        ) : labAdm.length === 0 ? (
                          <span style={{ fontSize: '12px', color: '#adb5bd', fontStyle: 'italic' }}>
                            No hay exámenes cargados para esta admisión.
                          </span>
                        ) : (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {labAdm.map(lab => {
                              const ext = lab.nombre_archivo?.split('.').pop().toLowerCase();
                              const cfg = {
                                pdf:  { bg:'#f8d7da', color:'#721c24', lbl:'PDF'  },
                                docx: { bg:'#cce5ff', color:'#004085', lbl:'WORD' },
                                doc:  { bg:'#cce5ff', color:'#004085', lbl:'WORD' },
                                jpg:  { bg:'#d4edda', color:'#155724', lbl:'IMG'  },
                                jpeg: { bg:'#d4edda', color:'#155724', lbl:'IMG'  },
                                png:  { bg:'#d4edda', color:'#155724', lbl:'IMG'  },
                              }[ext] || { bg:'#e2e3e5', color:'#383d41', lbl:'FILE' };

                              return (
                                <div key={lab.id} style={{
                                  background: '#fff', border: '1px solid #9fe1cb',
                                  borderLeft: '3px solid #1D9E75',
                                  borderRadius: '7px', padding: '10px 12px',
                                  minWidth: '200px', maxWidth: '260px',
                                  display: 'flex', flexDirection: 'column', gap: '4px',
                                }}>
                                  {/* Tipo badge + nombre */}
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                                    <span style={{ display: 'inline-block', padding: '2px 6px', borderRadius: '4px', fontSize: '9px', fontWeight: '800', background: cfg.bg, color: cfg.color }}>
                                      {cfg.lbl}
                                    </span>
                                    <span style={{ fontSize: '11px', fontWeight: '700', color: '#0F6E56', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {lab.tipo_examen}
                                    </span>
                                  </div>
                                  <div style={{ fontSize: '10px', color: '#6c757d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {lab.nombre_archivo}
                                  </div>
                                  <div style={{ fontSize: '10px', color: '#adb5bd' }}>
                                    {new Date(lab.fecha).toLocaleString('es-SV')}
                                  </div>
                                  {lab.notas && (
                                    <div style={{ fontSize: '10px', color: '#495057', fontStyle: 'italic', marginTop: '2px' }}>
                                      {lab.notas}
                                    </div>
                                  )}
                                  <button
                                    style={{ background: '#1D9E75', color: '#fff', border: 'none', borderRadius: '5px', padding: '5px 10px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', marginTop: '4px', alignSelf: 'flex-start' }}
                                    onClick={() => window.open(api.lab.urlArchivo(lab.ruta_archivo), '_blank')}>
                                    Abrir archivo
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Componente principal ─────────────────────────────────────
export default function ExpedientePaciente({ usuario, onVerCuenta, onAbrirFormulario, navInicial }) {
  // Financiero — solo admin/caja (ver nota en DetallePaciente más abajo).
  const verFacturacion = ['admin', 'caja'].includes(usuario?.rol);

  const [pacientes,   setPacientes]   = useState([]);
  const [busqueda,    setBusqueda]    = useState('');
  const [anioFiltro,  setAnioFiltro]  = useState(String(HOY.getFullYear()));
  const [mesFiltro,   setMesFiltro]   = useState('');
  const [cargando,    setCargando]    = useState(false);
  const [seleccionado,setSeleccionado]= useState(null);
  // Sección a la que hay que hacer scroll dentro de DetallePaciente al llegar
  // por navegación dirigida (ej. desde "Mi panel" → Labs pendientes). Se limpia
  // una vez consumida para no re-disparar el scroll en re-renders posteriores.
  const [irASeccion,  setIrASeccion]  = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    const r = await api.expedientes.listar({ anio: anioFiltro, mes: mesFiltro });
    if (r.ok) setPacientes(r.data);
    setCargando(false);
  }, [anioFiltro, mesFiltro]);

  useEffect(() => { cargar(); }, [cargar]);

  // Navegación dirigida: si llega navInicial con pacienteId, resolver el
  // paciente (sin depender de que ya esté en la lista cargada/filtrada)
  // y auto-seleccionarlo, saltando directo a su expediente.
  useEffect(() => {
    if (!navInicial?.pacienteId) return;
    let activo = true;
    (async () => {
      const r = await api.expedientes.obtenerPaciente(navInicial.pacienteId);
      if (activo && r.ok && r.data) {
        setSeleccionado(r.data);
        setIrASeccion(navInicial.irA || null);
      }
    })();
    return () => { activo = false; };
  }, [navInicial]);

  // Búsqueda local
  const filtrados = pacientes.filter(p => {
    if (!busqueda) return true;
    const q = busqueda.toLowerCase();
    return (
      `${p.nombre} ${p.apellido}`.toLowerCase().includes(q) ||
      (p.codigo || '').toLowerCase().includes(q) ||
      (p.dui || '').includes(q)
    );
  });

  const aniosDisponibles = [];
  for (let a = 2020; a <= HOY.getFullYear() + 1; a++) aniosDisponibles.push(a);

  if (seleccionado) {
    return (
      <div style={{ fontFamily:'Segoe UI, sans-serif' }}>
        <div style={{ background:'linear-gradient(135deg,#1B2A5E,#0f1c40)', padding:'16px 24px' }}>
          <div style={{ fontSize:'19px', fontWeight:'800', color:'#fff', marginBottom:'3px' }}>
            📂 Módulo de Expedientes
          </div>
          <div style={{ fontSize:'12px', color:'rgba(255,255,255,.8)' }}>
            Archivo clínico · Historial de admisiones · Notas y documentos
          </div>
        </div>
        <div style={S.pagina}>
        <DetallePaciente
          paciente={seleccionado}
          usuario={usuario}
          onVolver={() => { setSeleccionado(null); setIrASeccion(null); }}
          onVerAdmision={(id) => onVerCuenta && onVerCuenta(id)}
          onAbrirFormulario={onAbrirFormulario}
          irASeccion={irASeccion}
        />
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily:'Segoe UI, sans-serif' }}>
      {/* ══ Header módulo — estandarizado ══ */}
      <div style={{ background:'linear-gradient(135deg,#1B2A5E,#0f1c40)', padding:'16px 24px' }}>
        <div style={{ fontSize:'19px', fontWeight:'800', color:'#fff', marginBottom:'3px' }}>
          📂 Módulo de Expedientes
        </div>
        <div style={{ fontSize:'12px', color:'rgba(255,255,255,.8)' }}>
          Archivo clínico · Historial de admisiones · Notas y documentos
        </div>
      </div>

      <div style={S.pagina}>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: verFacturacion ? 'repeat(4,1fr)' : 'repeat(3,1fr)', gap: '12px', marginBottom: '16px' }}>
        <div style={S.kpi}><div style={S.kpiVal}>{pacientes.length}</div><div style={S.kpiLabel}>Pacientes {anioFiltro || 'totales'}</div></div>
        <div style={S.kpi}><div style={S.kpiVal}>{pacientes.reduce((s,p) => s + (p.total_admisiones || 0), 0)}</div><div style={S.kpiLabel}>Atenciones registradas</div></div>
        <div style={S.kpi}><div style={S.kpiVal}>{pacientes.filter(p => p.tiene_activa).length}</div><div style={S.kpiLabel}>Con ingreso activo</div></div>
        {/* Financiero — solo admin/caja */}
        {verFacturacion && (
          <div style={S.kpi}><div style={{ ...S.kpiVal, fontSize: '16px' }}>${pacientes.reduce((s,p) => s + Number(p.total_facturado || 0), 0).toFixed(2)}</div><div style={S.kpiLabel}>Total facturado período</div></div>
        )}
      </div>

      <div style={S.card}>
        <div style={S.titulo}>
          <span>Archivo clínico de pacientes ({filtrados.length})</span>
          <button style={S.btnSecundario} onClick={cargar}>Actualizar</button>
        </div>

        {/* Filtros */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input style={{ ...S.input, maxWidth: '280px' }}
            placeholder="Buscar por nombre, código o DUI..."
            value={busqueda} onChange={e => setBusqueda(e.target.value)} />

          <div>
            <select style={{ ...S.input, width: 'auto' }}
              value={anioFiltro} onChange={e => setAnioFiltro(e.target.value)}>
              <option value="">Todos los años</option>
              {aniosDisponibles.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>

          <div>
            <select style={{ ...S.input, width: 'auto' }}
              value={mesFiltro} onChange={e => setMesFiltro(e.target.value)}>
              <option value="">Todos los meses</option>
              {MESES.map((m,i) => <option key={i} value={i+1}>{m}</option>)}
            </select>
          </div>
        </div>

        {cargando ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>Cargando expedientes...</div>
        ) : (
          <table style={S.tabla}>
            <thead>
              <tr>
                {['Código', 'Paciente', 'DUI', 'Teléfono', 'Seguro', 'Atenciones', 'Última visita', 'Facturado', 'Estado', ''].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtrados.length === 0 ? (
                <tr><td colSpan="10" style={{ ...S.td, textAlign: 'center', padding: '30px', color: '#6c757d' }}>
                  No hay pacientes para el período seleccionado.
                </td></tr>
              ) : filtrados.map(p => (
                <tr key={p.id} style={{ cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafd'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  onClick={() => setSeleccionado(p)}>
                  <td style={S.td}>
                    <span style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: '700', color: '#1B2A5E', background: '#e8f0fb', padding: '2px 7px', borderRadius: '4px' }}>
                      {p.codigo || 'PAC-' + String(p.id).padStart(5,'0')}
                    </span>
                  </td>
                  <td style={{ ...S.td, fontWeight: '600' }}>{p.nombre} {p.apellido}</td>
                  <td style={{ ...S.td, color: '#6c757d', fontSize: '12px' }}>{p.dui || '—'}</td>
                  <td style={{ ...S.td, color: '#6c757d', fontSize: '12px' }}>{p.telefono || '—'}</td>
                  <td style={S.td}>{p.seguro_nombre || 'Ninguno'}</td>
                  <td style={{ ...S.td, textAlign: 'center', fontWeight: '700' }}>{p.total_admisiones || 0}</td>
                  <td style={{ ...S.td, fontSize: '12px' }}>
                    {p.ultima_visita ? new Date(p.ultima_visita).toLocaleDateString('es-SV') : '—'}
                  </td>
                  <td style={{ ...S.td, fontWeight: '700', color: '#1B2A5E' }}>
                    ${Number(p.total_facturado || 0).toFixed(2)}
                  </td>
                  <td style={S.td}>
                    {p.tiene_activa
                      ? <span style={S.pill('verde')}>Activo</span>
                      : <span style={S.pill('gris')}>Sin ingreso</span>}
                  </td>
                  <td style={S.td}>
                    <button style={{ ...S.btnPrimario, fontSize: '11px', padding: '5px 12px' }}
                      onClick={e => { e.stopPropagation(); setSeleccionado(p); }}>
                      Ver expediente
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      </div>
    </div>
  );
}
